<template>
    <div>
        <BaseDialog
            v-model:open="isVisible"
            title="Anybody home?"
            description="Your session is about to expire."
            disable-close-on-backdrop-click
            overlay-class="z-[80]"
            dialog-content-class="z-[90] md:w-[480px]">
            <div class="flex flex-col items-start gap-5 p-6 md:gap-8 md:p-10">
                <h2 class="title">Anybody home?</h2>
                <p class="body text-ink-muted">Your session is about to expire.</p>
                <div class="flex w-full justify-end">
                    <BaseButton theme="primary" @click="onRenewSession">Wait, I'm still here!</BaseButton>
                </div>
            </div>
        </BaseDialog>

        <!-- Hidden logout form -->
        <form ref="logoutForm" method="POST" action="/accounts/logout/" style="display: none">
            <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
            <input type="hidden" name="next" :value="nextUrl" />
        </form>
    </div>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { reportSessionEnded, SESSION_EXPIRED } from "~vue/utils/sessionEnded";
import { DateTime } from "luxon";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { getCookie } from "../utils";

/** How frequently to check for an about-to-expire session, in milliseconds. */
const SESSION_CHECK_INTERVAL_MS = 10_000;

/** The session warning dialog will appear this many milliseconds before the session expiration time. */
const SESSION_SHOW_WARNING_DIALOG_MS = 60_000;

const EXPIRATION_COOKIE_NAME = "session_expires_at";

// Mounted at the app root (not in a layout) so pages without the sidebar get the warning too, and
// on the same z-80/z-90 tier as SessionEndedDialog so it paints above side panels and other dialogs.
// Backdrop and escape are disabled on purpose: the only way out is renewing or being logged out.

const logoutForm = ref(null);
const timer = ref(null);
const isVisible = ref(false);

const nextUrl = computed(() => window.location.pathname + window.location.search);

async function checkExpiry() {
    if (!window.cookieStore) {
        return;
    }

    const cookie = await window.cookieStore.get(EXPIRATION_COOKIE_NAME);
    if (!cookie || !cookie.value) {
        return;
    }

    const now = DateTime.now();
    const sessionExpiresAt = DateTime.fromISO(cookie.value);
    if (sessionExpiresAt < now) {
        return reportExpired();
    }

    const msRemaining = sessionExpiresAt - now;
    isVisible.value = msRemaining <= SESSION_SHOW_WARNING_DIALOG_MS;
}

// setInterval is throttled and then fully paused when the browser freezes or discards a
// hidden tab (Chrome's Page Lifecycle), so a session that lapses while the user is away is
// never noticed by the timer alone. Re-check the instant the tab becomes visible again to
// catch an expiry that happened while it was frozen.
function onVisibilityChange() {
    if (document.visibilityState === "visible") {
        checkExpiry();
    }
}

onMounted(() => {
    timer.value = setInterval(checkExpiry, SESSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
});

onUnmounted(() => {
    clearInterval(timer.value);
    document.removeEventListener("visibilitychange", onVisibilityChange);
});

async function onRenewSession() {
    try {
        const response = await fetch("/api/accounts/renew-session", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: "{}",
        });
        if (!response.ok) {
            return logout();
        }
    } finally {
        isVisible.value = false;
    }
}

/**
 * Hand an already-expired session to SessionEndedDialog, which offers a way back in.
 *
 * Deliberately not the logout form: logging out is a CSRF-protected POST, and with
 * CSRF_USE_SESSIONS the secret lives in the session that has just expired -- so that POST is
 * rejected with a 403 and the user is stranded on an error page (BUG-942). There is nothing to
 * tear down server-side either, since the session is already gone.
 *
 * Not a direct navigation either. The expiry cookie is only refreshed on responses that also set
 * the session cookie, so it can read as past while the session is in fact still alive (likewise
 * under a fast client clock). Navigating on that would yank the user out of a page they were
 * still entitled to; a dialog leaves the decision, and the page, with them (BUG-947).
 */
function reportExpired() {
    clearInterval(timer.value);
    isVisible.value = false;
    reportSessionEnded(SESSION_EXPIRED, usePage().props?.session_ended_login_url ?? null);
}

function logout() {
    if (logoutForm.value) {
        // Replace current history entry so back button can't return to this page
        window.history.replaceState(null, "", window.location.href);
        logoutForm.value.submit();
    }
}
</script>
